import React from "react";
import { createRoot } from "react-dom/client";
import toast, { Toaster } from "react-hot-toast";

import {
  QueryClient,
  QueryClientProvider,
  HydrationBoundary,
} from "@tanstack/react-query";

import "./index.css";
import { Auth0ProviderWithHistory } from "@/auth";
import { KBarProvider, KBarContent, KBarSearch, KBarResults } from "kbar";

import { BrowserRouter as Router } from "react-router-dom";

import App from "./App";
import { AttributionTracker } from "@/utils/attribution";
import reportWebVitals from "./reportWebVitals";
import { WebSocketProvider, NotificationProvider } from "@moonscape/shared";

// Before React mounts: a protected route can start the Auth0 redirect from a
// child effect, ahead of App's AttributionTracker.init(). Snapshot the raw
// referrer + landing URL now so the first touch survives the round trip.
AttributionTracker.snapshotFirstLoad();

/**
 * Toasts speak the alert-box language: square corners, hairline border, a 3px
 * left accent carrying the tone, paper ground, no icon chip, no blue.
 * docs/design/newia-web/Alerts.html
 */
const TOAST_BOX = {
  borderRadius: 0,
  border: "1px solid var(--ms-line)",
  borderLeftWidth: "3px",
  borderLeftStyle: "solid",
  borderLeftColor: "var(--ms-line2)",
  background: "var(--ms-card)",
  color: "var(--ms-mute)",
  fontSize: "12.5px",
  lineHeight: 1.5,
  padding: "12px 15px",
  maxWidth: "440px",
  boxShadow: "none",
};

const TOAST_OPTIONS = {
  style: TOAST_BOX,
  // quiet
  blank: { icon: null, style: { borderLeftColor: "var(--ms-line2)" } },
  // live
  success: { icon: null, style: { borderLeftColor: "var(--ms-rule)" } },
  // problem
  error: { icon: null, style: { borderLeftColor: "var(--ms-neg)" } },
  // the spinner is live state, not decoration — it stays
  loading: { style: { borderLeftColor: "var(--ms-rule)" } },
};

// Suppress ResizeObserver errors (benign errors that occur during route transitions)
const debounce = (callback, delay) => {
  let tid;
  return function (...args) {
    const ctx = this;
    tid && clearTimeout(tid);
    tid = setTimeout(() => {
      callback.apply(ctx, args);
    }, delay);
  };
};

const originalOnError = window.onerror;
window.onerror = debounce(function(msg, url, lineNo, columnNo, error) {
  // Suppress ResizeObserver errors
  if (msg === "ResizeObserver loop completed with undelivered notifications.") {
    return false;
  }
  // Call original error handler for other errors
  if (originalOnError) {
    return originalOnError.call(window, msg, url, lineNo, columnNo, error);
  }
  return false;
}, 50);

const QUERY_CACHE_STORAGE_KEY = "queryCache";
const MAX_CACHE_BYTES = 1024 * 1024; // 1MB cap to avoid localStorage quota errors
const MAX_QUERY_BYTES = 128 * 1024; // Skip large queries

const isPricingErrorsListQuery = (queryKey) =>
  Array.isArray(queryKey) &&
  queryKey.length === 1 &&
  queryKey[0] === "pricing-errors";

const safeStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return null;
  }
};

// Create a react-query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchInterval: 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  },
  // This function is called when the client is created
  onHydrate: (data) => {
    // Load the cache from localStorage
    const cachedData = localStorage.getItem(QUERY_CACHE_STORAGE_KEY);
    if (cachedData) {
      const queries = JSON.parse(cachedData);
      queries.forEach((query) => {
        queryClient.setQueryData(query.queryKey, query.state.data);
      });
    }
  },
});
// This function is called whenever a query is successful
/** 
 * React Query's queryClient.getQueryCache().getAll() returns an object that
   contains circular references, which can't be serialized to JSON directly.

To solve this, you need to create a custom replacer function that can handle
circular references. However, it's important to note that React Query's cache
contains complex objects (like functions) that can't be serialized to JSON at
all. So, it's generally not recommended to serialize the entire cache.
 */
queryClient.getQueryCache().subscribe(() => {
  const queries = queryClient.getQueryCache().getAll();

  // In this example, we're only persisting the queryKey, data, and status of
  // each query. You might need to adjust this to fit your needs.
  const simplifiedQueries = [];
  let totalSize = 0;

  queries.forEach((query) => {
    if (isPricingErrorsListQuery(query.queryKey)) {
      return;
    }

    if (query.state.data === undefined) {
      return;
    }

    const entry = {
      queryKey: query.queryKey,
      state: {
        data: query.state.data,
        status: query.state.status,
      },
    };

    const serialized = safeStringify(entry);
    if (!serialized) {
      return;
    }

    if (serialized.length > MAX_QUERY_BYTES) {
      return;
    }

    if (totalSize + serialized.length > MAX_CACHE_BYTES) {
      return;
    }

    simplifiedQueries.push(entry);
    totalSize += serialized.length;
  });

  try {
    localStorage.setItem(
      QUERY_CACHE_STORAGE_KEY,
      JSON.stringify(simplifiedQueries)
    );
  } catch (error) {
    localStorage.removeItem(QUERY_CACHE_STORAGE_KEY);
  }
});

// KBar actions
const actions = [
  {
    id: "home",
    name: "Home",
    shortcut: ["i"],
    keywords: "trades home positions",
    perform: () => (window.location.pathname = "/"),
  },
  {
    id: "import",
    name: "Import",
    shortcut: ["i"],
    keywords: "import exchange api",
    perform: () => (window.location.pathname = "import"),
  },
  {
    id: "settings",
    name: "Settings",
    shortcut: ["s"],
    keywords: "settings",
    perform: () => (window.location.pathname = "settings"),
  },
  {
    id: "apikeys",
    name: "Exchange API Keys",
    shortcut: ["k"],
    keywords: "exchange keys api connection",
    perform: () => (window.location.pathname = "settings"),
  },
];

const searchStyles = {
  padding: "12px 16px",
  fontSize: "16px",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
  border: "none",
  background: "rgba(229, 231, 235, var(--tw-border-opacity))",
  color: "var(--foreground)",
};

const container = document.getElementById("root");
const root = createRoot(container); // createRoot(container!) if you use TypeScript
root.render(
  <React.StrictMode>
    <Auth0ProviderWithHistory>
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={queryClient.getQueryCache().getAll()}>
          {/* <KBarProvider
            actions={actions}
            options={{
              animations: {
                enterMs: 200,
                exitMs: 100,
                maxContentHeight: 400,
              },
            }}
          >
            <KBarContent
              contentStyle={{
                maxWidth: "400px",
                width: "100%",
                background: "var(--background)",
                color: "var(--foreground)",
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "var(--shadow)",
              }}
            >
              <KBarSearch
                style={searchStyles}
                placeholder="Type a command or search…"
              />
              <KBarResults />
            </KBarContent> */}
          <Toaster position="bottom-center" toastOptions={TOAST_OPTIONS} />
          <Router>
            <WebSocketProvider>
              <NotificationProvider>
                <App />
              </NotificationProvider>
            </WebSocketProvider>
          </Router>
          {/* </KBarProvider> */}
        </HydrationBoundary>
      </QueryClientProvider>
    </Auth0ProviderWithHistory>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
